import { formatTaiwanAddressAnswer } from '@/lib/taiwan-address'
import type { AnswerValue, Form, Question, ResponseSummary } from '@/types/form'
import { QuestionTypeSchema } from '@/types/form'
import { z } from 'zod'
import type { ContractSourceMeta } from './questionnaire'

export const ResponseStatusFilterSchema = z.union([
  z.literal('all'),
  z.literal('complete'),
  z.literal('partial'),
])
export type ResponseStatusFilter = z.infer<typeof ResponseStatusFilterSchema>

export const ReviewStatusSchema = z.enum([
  'not_required',
  'pending_review',
  'changes_requested',
  'approved',
  'rejected',
])
export type ReviewStatus = z.infer<typeof ReviewStatusSchema>

export const CustomerRecordUiStatusSchema = z.enum([
  'none',
  'not_filed',
  'filed',
  'duplicate_review',
])
export type CustomerRecordUiStatus = z.infer<typeof CustomerRecordUiStatusSchema>

export const reviewStatusLabels: Record<ReviewStatus, string> = {
  not_required: '不需審核',
  pending_review: '待主管審核',
  changes_requested: '需補件',
  approved: '已核准',
  rejected: '已退回',
}

export const customerRecordStatusLabels: Record<CustomerRecordUiStatus, string> = {
  none: '不適用',
  not_filed: 'ERP未建檔',
  filed: 'ERP已建檔',
  duplicate_review: '疑似重複',
}

export const AnswerRecordContractSchema = z.object({
  questionId: z.string(),
  questionTitle: z.string(),
  questionType: QuestionTypeSchema,
  value: z.unknown(),
  displayValue: z.string(),
  timeSpentSeconds: z.number().optional(),
  mappedField: z.string().optional(),
})
export type AnswerRecordContract = {
  questionId: string
  questionTitle: string
  questionType: z.infer<typeof QuestionTypeSchema>
  value: AnswerValue
  displayValue: string
  timeSpentSeconds?: number
  mappedField?: string
}

export type ResponseRecordContract = ResponseSummary & {
  formTitle: string
  submitterName: string
  submitterEmail: string
  ownerName: string
  sourceLabel: string
  reviewStatus: ReviewStatus
  customerStatus: CustomerRecordUiStatus
  reviewStatusLabel: string
  customerStatusLabel: string
  answerPreviews: AnswerRecordContract[]
  sourceMeta: ContractSourceMeta
}

export type SavedResponseFilterContract = {
  id: string
  label: string
  description: string
  params: Partial<ListResponseRecordsParams>
  sourceMeta: ContractSourceMeta
}

export const ListResponseRecordsParamsSchema = z.object({
  formId: z.string().optional(),
  keyword: z.string().optional(),
  status: ResponseStatusFilterSchema.default('all'),
  reviewStatus: z.union([z.literal('all'), ReviewStatusSchema]).default('all'),
  customerStatus: z.union([z.literal('all'), CustomerRecordUiStatusSchema]).default('all'),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  ownerName: z.string().optional(),
  previewQuestionCount: z.number().int().positive().max(20).default(3),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
})
export type ListResponseRecordsParams = z.input<typeof ListResponseRecordsParamsSchema>
export type NormalizedListResponseRecordsParams = z.output<typeof ListResponseRecordsParamsSchema>

export type ListResponseRecordsResult = {
  data: ResponseRecordContract[]
  meta: ContractSourceMeta & {
    total: number
    page: number
    limit: number
  }
}

export type ResponseDetailContract = ResponseRecordContract & {
  form: Pick<Form, 'id' | 'title' | 'description' | 'status'>
  questions: Question[]
  answersList: AnswerRecordContract[]
  hiddenFields: Record<string, string>
  metadataEntries: Array<{ key: string; value: string }>
}

export type ResponseSnapshotContract = {
  responseId: string
  formTitle: string
  submitter: {
    name: string
    email: string
  }
  statusLabel: string
  reviewStatusLabel: string
  customerStatusLabel: string
  answerCount: number
  metadataKeys: string[]
  exportMode: 'mock_snapshot'
  warning: string
  sourceMeta: ContractSourceMeta
}

export type ResponseExportFormat = 'csv' | 'xlsx' | 'json' | 'spss' | 'raw_package'

export const responseExportDataSourceValues = [
  'filed_customers',
  'all_customers',
  'all_responses',
] as const
export type ResponseExportDataSource = (typeof responseExportDataSourceValues)[number]

export const responseExportDataSourceLabels: Record<ResponseExportDataSource, string> = {
  filed_customers: '已建檔客戶',
  all_customers: '全部客戶',
  all_responses: '全部填寫記錄資料',
}

export type ResponseExportFilters = {
  filingDateFrom?: string
  filingDateTo?: string
}

export type ResponseExportDetailColumn = {
  id: string
  label: string
  group: 'metadata' | 'answers' | 'hiddenFields' | 'customer'
  spreadsheetFormat?: 'text'
}

export type ResponseExportDetailRow = {
  id: string
  recordNo: string
  responseId: string
  submittedAt: string
  statusLabel: string
  respondentLabel: string
  cells: Record<string, string>
}

export type ResponseExportDetailContract = {
  format: Extract<ResponseExportFormat, 'csv' | 'json'>
  label: string
  filename: string
  contentType: string
  jobMode: 'd1_detail_rows'
  providerState: 'configured'
  fileGeneration: 'browser_local'
  includes: Array<'responses' | 'answers' | 'metadata' | 'customerFields'>
  warning: string
  formId: string
  formTitle: string
  rowCount: number
  rowLimit: number
  page: number
  pageSize: number
  rowOffset: number
  totalRows: number
  totalPages: number
  hasNextPage: boolean
  dataSource: ResponseExportDataSource
  dataSourceLabel: string
  filters?: ResponseExportFilters
  columns: ResponseExportDetailColumn[]
  rows: ResponseExportDetailRow[]
  sourceMeta: ContractSourceMeta
}

export const RESPONSE_EXPORT_SERVER_REDACTION_POLICY =
  'owner_approved_full_detail_admin_export+raw_rows_not_stored' as const

export type ResponseExportServerFormat = Extract<ResponseExportFormat, 'csv' | 'json'>

export type ResponseExportServerDelivery = 'browser_local' | 'r2_signed_url' | 'worker_stream'

export type ResponseExportServerJobStatus = 'queued' | 'processing' | 'ready' | 'failed' | 'expired'

export type ResponseExportServerDownloadMode = 'signed_url' | 'worker_stream'

export type ResponseExportServerJobRequest = {
  format: ResponseExportServerFormat
  delivery: ResponseExportServerDelivery
  limit: number
  includeSensitiveFields: boolean
  dataSource?: ResponseExportDataSource
  filingDateFrom?: string
  filingDateTo?: string
}

export type ResponseExportServerSafeJobContract = {
  exportJobId: string
  status: ResponseExportServerJobStatus
  format: ResponseExportServerFormat
  fileName: string
  rowCount: number
  rowLimit: number
  chunkSize: number
  partCount: number
  downloadMode: ResponseExportServerDownloadMode
  downloadUrlExpiresAt?: string
  includeSensitiveFields: boolean
  redactionPolicy: typeof RESPONSE_EXPORT_SERVER_REDACTION_POLICY
  createdAt?: string
  expiresAt?: string
  safeErrorCode?: string
  retryable?: boolean
}

export type ResponseExportServerDeleteResultContract = {
  status: 'deleted'
  exportJobId: string
  deletedAt: string
  r2DeletedCount: number
}

export function stringifyAnswerValue(value: AnswerValue): string {
  if (value === null || value === undefined) return '未填寫'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') {
    const address = formatTaiwanAddressAnswer(value)
    if (address) return address
    return Object.entries(value)
      .map(([key, item]) => `${key}: ${Array.isArray(item) ? item.join(', ') : String(item)}`)
      .join(' / ')
  }
  return String(value)
}
